import { ArrowRightOutlined, CopyFilled } from '@ant-design/icons';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XIcon,
} from '@heroicons/react/solid';
import {
  Breakpoint,
  Button,
  Col,
  Divider,
  Drawer,
  Input,
  message,
  Modal,
  Row,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import ShareIconVideo from 'assets/Icons/ShareIconVideo';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import { useRouter } from 'next/router';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { shareModalVisibleState } from '@/atoms/modalAtom';
import { normalizeProductName } from '@/utils/index';
import { IProduct } from '@/utils/interfaces/Product';

import { ShouldRender } from '../shared/basic/ShouldRender';
import { notify } from '../shared/notify';
import { authModalControl } from '../../atoms/authModalControl';
import { user } from '../../atoms/user';
import { type EventDetails, logEvent } from '../../hooks/useClickEventTracking';
import type { User } from '../../utils/interfaces/User';
import {
  BoxGift,
  codeSlash,
  coinFill,
  copyFile,
  discordIcon,
  email,
  facebook,
  FacebookMessenger,
  iMessage,
  linkedIn,
  linkedInBuilding,
  MicrosoftTeams,
  pinterest,
  redit,
  shareForwardFill,
  sitemap,
  skype,
  slack,
  Telegram,
  Tumblr,
  twitter,
  userFollowFill,
  WhatsApp,
  youtube,
} from '../../../assets';
import ShareIcon from '../../../assets/ShareIcons/ShareIcon';

type ShareButtonType =
  | 'invite'
  | 'book'
  | 'school'
  | 'no-icon'
  | 'only-icon-title'
  | 'video-book'
  | 'default';

const SHARE_URLS = {
  Facebook: (url: string, title: string) =>
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&t=${encodeURIComponent(title)}`,
  LinkedIn: (url: string, title: string) =>
    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
  'X(Twitter)': (url: string, title: string) =>
    `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
  Pinterest: (url: string, title: string, image: string) =>
    `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&description=${encodeURIComponent(title)}&media=${encodeURIComponent(image)}`,
  Discord: (url: string, title: string) =>
    `https://discord.com/channels/@me?text=${encodeURIComponent(title + ' ' + url)}`,
  Reddit: (url: string, title: string) =>
    `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
  Tumblr: (url: string, title: string) =>
    `https://www.tumblr.com/widgets/share/tool?canonicalUrl=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
  Email: (url: string, title: string) =>
    `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`,
  WhatsApp: (url: string, title: string) =>
    `https://api.whatsapp.com/send?text=${encodeURIComponent(title + ' ' + url)}`,
  Telegram: (url: string, title: string) =>
    `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  Slack: (url: string) =>
    `https://slack.com/app_redirect?channel=general&team=your-team-id&text=${encodeURIComponent(url)}`,
  Skype: (url: string) =>
    `https://web.skype.com/share?url=${encodeURIComponent(url)}`,
  iMessage: (url: string, title: string) =>
    `sms:&body=${encodeURIComponent(`${title} ${url}`)}`,
};

const REFERRAL_STEPS = [
  {
    icon: 'share',
    text: 'You share your referral link with friends.',
  },
  {
    icon: 'user',
    text: 'Your friends sign up on the app and verify their email.',
  },
  {
    icon: 'gift',
    text: 'You and your friends will be rewarded with 100 Z coins.',
  },
  {
    icon: 'coin',
    text: 'Redeem products by using Z-coins.',
  },
];

interface ShareButtonProps {
  samplePdf?: string;
  isFullScreen?: boolean;
  onModalClose?: () => void;
  isModalInitiallyOpen?: boolean;
  isReadingScreen?: boolean;
  isLoggedIN: boolean;
  isInviteFriend?: boolean;
  isBookLandingPage?: boolean;
  isSchoolPage?: boolean;
  isShortBook?: boolean;
  noBg?: boolean;
  hideIcon?: boolean;
  type?: ShareButtonType;
  isPdfReader?: boolean;
  productId?: string;
  togglePlayer?: () => void;
  ecomProductData?: IProduct | null | undefined;
}

interface ShareButtonRenderProps {
  type: ShareButtonType;
  openShareModal: () => void;
  isReadingScreen?: boolean;
  noBg?: boolean;
  screens: Partial<Record<Breakpoint, boolean>>;
  isPdfReader?: boolean;
}

const ShareButtonRender = ({
  type,
  openShareModal,
  isReadingScreen = false,
  noBg = false,
  screens,
}: ShareButtonRenderProps) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    openShareModal();
  };

  switch (type) {
    case 'invite':
      return (
        <Button
          ghost
          onClick={handleClick}
          className='bg-transparent border border-primary text-darkPrimary font-semibold rounded-lg px-4 py-1.5 h-auto text-sm flex items-center gap-1 hover:opacity-80'
        >
          Invite a Friend <ChevronRightIcon className='w-5 h-5' />
        </Button>
      );

    case 'book':
      return (
        <Button
          onClick={handleClick}
          icon={<ShareIcon width='18' />}
          className={clsx(
            'p-0 lg:px-3',
            'border-none rounded-full bg-transparent lg:bg-[#282829] text-sm font-semibold text-white hover:opacity-80',
          )}
        />
      );

    case 'school':
      return (
        <Button
          onClick={handleClick}
          icon={<ShareIcon width='16 ' />}
          className={clsx(
            'p-0 lg:py-3 lg:px-5 h-10',
            'border-none rounded-lg bg-transparent text-sm flex items-center font-semibold text-white hover:opacity-80',
          )}
        >
          {screens.sm && 'Share'}
        </Button>
      );

    case 'video-book':
      return (
        <Button
          onClick={handleClick}
          icon={
            <ShareIconVideo
              width={!screens.md ? 28 : 40}
              height={!screens.md ? 28 : 40}
            />
          }
          className={clsx(
            'p-0 lg:py-3 lg:px-5 h-10',
            'border-none rounded-lg bg-transparent text-sm flex items-center font-semibold text-white hover:opacity-80',
          )}
        ></Button>
      );
    case 'no-icon':
      return null;

    case 'only-icon-title':
      return (
        <div className='flex flex-col items-center space-y-1'>
          <button
            className={clsx(
              'w-12 h-12 rounded-full flex items-center justify-center transition hover:opacity-80',
              'bg-grayZl-850',
              'text-white',
            )}
            onClick={handleClick}
          >
            <ShareIcon width='16 ' />
          </button>
          <span className='text-xs text-white font-medium text-center'>
            Share
          </span>
        </div>
      );

    case 'default':
    default:
      return (
        <Button
          onClick={handleClick}
          icon={<ShareIcon width='18' />}
          className={clsx(
            !isReadingScreen ? 'p-0 lg:p-3 h-10' : 'p-0 lg:px-3',
            !noBg && 'bg-transparent lg:bg-[#282829] border-none',
            'border-none rounded-full text-sm font-semibold text-white hover:opacity-80',
          )}
        >
          {screens.lg && 'Share'}
        </Button>
      );
  }
};

const ShareButton: React.FC<ShareButtonProps> = ({
  samplePdf,
  isFullScreen,
  onModalClose,
  isReadingScreen = false,
  isLoggedIN,
  isShortBook = false,
  noBg = false,
  type = 'default',
  productId,
  isPdfReader = false,
  togglePlayer,
  ecomProductData,
}) => {
  const [isModalVisible, setIsModalVisible] = useRecoilState(
    shareModalVisibleState,
  );
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState('https://zebralearn.com/dl/');
  const [shareTitle, setShareTitle] = useState(
    'Check out this awesome content!',
  );
  const [isCopyingLink, setIsCopyingLink] = useState(false);
  const [loadingQuickAction, setLoadingQuickAction] = useState(false);
  const [quickActionInput, setQuickActionInput] = useState('');
  const [messageApi, contextHolder] = message.useMessage();
  const router = useRouter();
  const [dropdownState, setDropdownState] = useState<
    'main' | 'preparing' | 'copied' | 'howItWorks' | 'embed'
  >('main');
  const [, setAuthModalVisible] = useRecoilState(authModalControl);
  const screens = useBreakpoint();
  const [pageTitle, setPageTitle] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const userData: User | undefined = useRecoilValue(user);
  const zebraLearnBaseUrl =
    process.env.NEXT_PUBLIC_APP_ENV === 'production'
      ? 'https://zebralearn.com'
      : 'https://staging.zebralearn.com';
  const { bookId } = router.query;

  const extractId = (path: string | undefined): string | undefined => {
    if (path) {
      const parts = path.split('-');
      return parts[parts.length - 1];
    } else if (productId) return productId;
    else return undefined;
  };

  const handleCopyEmbedLink = (url: string) => {
    navigator.clipboard
      .writeText(
        `<iframe
  src='${url}'
  allow="fullscreen;"
  width="100%"
  height="600px"
  title="PDF Reader"
  loading="lazy"
  sandbox="allow-same-origin allow-scripts allow-popups"
></iframe>`,
      )
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((error) => {
        console.error('Error copying link to clipboard: ', error);
      });
  };

  useEffect(() => {
    if (
      isModalVisible &&
      router.asPath.includes('books') &&
      typeof document !== 'undefined'
    ) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isModalVisible, router.asPath]);

  useEffect(() => {
    if (!router.isReady) return;
    const refCode = userData?.ref_code;
    const basePath = zebraLearnBaseUrl;
    let url = '';
    if (isReadingScreen) {
      const query = isShortBook ? '?short=true' : '';
      url = `${basePath}/books/zebralearn-${extractId(bookId as string)}${query}${query ? '&' : '?'}ref_code=${refCode}`;
    } else {
      const separator = router.asPath.includes('?') ? '&' : '?';
      url = `${basePath}${router.asPath}${separator}ref_code=${refCode}`;
    }
    setShareUrl(url);

    if (typeof document !== 'undefined' && document.title) {
      setShareTitle(document.title);
      setPageTitle(document.title);
    } else if (bookId) {
      const defaultTitle = `Check out: ${bookId}`;
      setShareTitle(defaultTitle);
      setPageTitle(defaultTitle);
    }
  }, [
    router.isReady,
    zebraLearnBaseUrl,
    userData?.ref_code,
    bookId,
    isReadingScreen,
    isShortBook,
    router.asPath,
  ]);

  const handleShare = (name: string) => {
    handleShareClick(name?.toLowerCase());
    if (name === 'Copy to clipboard') {
      navigator.clipboard
        .writeText(shareUrl)
        .then(() => {
          messageApi.success('Link copied to clipboard!');
        })
        .catch((err) => {
          messageApi.error('Failed to copy link');
          console.error('Could not copy text: ', err);
        });
      return;
    }
    if (name === 'Embed') {
      setDropdownState('embed');
      return;
    }
    if (name === 'Affiliate Marketing') {
      window.open('https://affiliate.zebralearn.com/', '_blank');
      return;
    }
    if (name === 'Print/PDF') {
      window.print();
      return;
    }
    const shareFunction = SHARE_URLS[name as keyof typeof SHARE_URLS];
    if (shareFunction) {
      const pinterestImage = '';
      const urlToShare =
        name === 'Pinterest'
          ? shareFunction(shareUrl, shareTitle, pinterestImage)
          : shareFunction(shareUrl, shareTitle, '');
      window.open(urlToShare, '_blank', 'width=600,height=400');
      messageApi.success(`Shared via ${name}!`);
    } else {
      messageApi.info(`${name} sharing coming soon!`);
    }
  };

  const handleCopyShareLink = () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      messageApi?.error?.('Clipboard not supported in this environment');
      return;
    }
    if (!shareUrl) {
      messageApi?.error?.('Nothing to copy');
      return;
    }
    handleShareClick('copy_main_share_link');
    setIsCopyingLink(true);
    setDropdownState('preparing');
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        setTimeout(() => {
          setDropdownState('copied');
          setIsCopyingLink(false);
        }, 1500);
      })
      .catch((err) => {
        console.error('Could not copy text: ', err);
        messageApi?.error?.('Failed to copy link');
        setDropdownState('main');
        setIsCopyingLink(false);
      });
  };

  const handleHowItWorks = () => {
    setDropdownState('howItWorks');
  };

  const SocialIcon = ({
    name,
    icon,
    color,
  }: {
    name: string;
    icon?: string;
    color: string;
  }) => (
    <motion.div
      className='flex flex-col items-center gap-1 cursor-pointer'
      onClick={() => handleShare(name)}
      whileTap={{ scale: 0.95 }}
    >
      <motion.div
        className='w-12 h-12 flex items-center border-[#282829] border-[1px] bg-black rounded-full justify-center overflow-hidden'
        whileHover={{
          backgroundColor: color,
          borderColor: color,
          boxShadow: '0 0 8px rgba(255, 255, 255, 0.3)',
        }}
        transition={{ duration: 0.2 }}
      >
        {icon && (
          <Image
            src={icon}
            alt={name}
            width={name !== 'LinkedIn Page' ? 20 : 24}
            height={name !== 'LinkedIn Page' ? 20 : 24}
            style={{ objectFit: 'contain' }}
          />
        )}
      </motion.div>
      <div className='text-white text-center text-xs'>{name}</div>
    </motion.div>
  );

  const isEmail = (value: string) => /\S+@\S+\.\S+/.test(value);
  const isPhone = (value: string) => /^\+?\d{10,15}$/.test(value);

  const handleQuickActionSubmit = async () => {
    setLoadingQuickAction(true);
    try {
      if (isEmail(quickActionInput)) {
        const subject = encodeURIComponent('Quick Action Link');
        const body = encodeURIComponent(`${shareTitle}: ${shareUrl}`);
        window.location.href = `mailto:${quickActionInput}?subject=${subject}&body=${body}`;
      } else if (isPhone(quickActionInput)) {
        const messageText = encodeURIComponent(`${shareTitle}: ${shareUrl}`);
        window.location.href = `sms:${quickActionInput}?&body=${messageText}`;
      } else {
        message.error('Please enter a valid email or phone number');
      }
    } catch (error) {
      message.error('Could not open messaging app');
    } finally {
      setLoadingQuickAction(false);
    }
  };

  const IconMap: Record<'gift' | 'coin' | 'share' | 'user', any> = {
    gift: BoxGift,
    coin: coinFill,
    share: shareForwardFill,
    user: userFollowFill,
  };

  const ReferralStepIcon = ({ icon }: { icon: string }) => (
    <motion.div
      className='w-10 h-10 rounded-full bg-[#282829] p-2 flex items-center justify-center'
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', damping: 15 }}
    >
      {icon && (
        <Image
          src={IconMap[icon as keyof typeof IconMap]}
          alt={icon}
          width={24}
          height={24}
          style={{ objectFit: 'contain' }}
        />
      )}
    </motion.div>
  );

  const originalContainerParentStyle = `${isFullScreen ? 'h-full  w-full ' : 'h-full  md:max-h-[70vh] lg:rounded-xl  w-full '} p-6`;

  const renderModalContent = () => {
    const modalContentWrapperStyle = `bg-[#1A1A1A] text-white w-full h-full overflow-y-auto`;

    if (dropdownState === 'preparing') {
      return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className='p-6   h-full  flex justify-between w-full flex-col rounded-t-xl md:max-h-[70vh] rounded-xl '
          style={{ backgroundColor: '#1A1A1A', borderRadius: 12 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className='flex items-center gap-2 mb-4'>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setDropdownState('main')}
              className='text-white p-1 hover:bg-gray-700 rounded-full'
            >
              <ArrowRightOutlined style={{ transform: 'rotate(180deg)' }} />
            </motion.button>
            <span className='font-semibold'>Copy to clipboard</span>
          </div>
          <div className='text-center mb-4'>Preparing your page</div>
          <motion.div
            className='h-1 bg-[#282829] rounded-full overflow-hidden mb-4'
            initial={{ width: '100%' }}
          >
            <motion.div
              className='h-full bg-[#FF5722]'
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 1.5 }}
            />
          </motion.div>
          <Button
            className='w-full bg-[#282829] border-none text-white hover:bg-gray-600'
            onClick={() => setDropdownState('main')}
          >
            Cancel
          </Button>
        </motion.div>
      );
    }
    if (dropdownState === 'embed') {
      const secretCode =
        typeof window !== 'undefined'
          ? btoa(process.env.NEXT_PUBLIC_IFRAME_SECRET_KEY || 'default-secret')
          : '';
      const embedUrl = `${zebraLearnBaseUrl}/pdfReader/${extractId(bookId as string)}/${secretCode}?code=AOCSTMAB`;
      return (
        <motion.div
          key='embed'
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
          className={clsx(
            originalContainerParentStyle,
            modalContentWrapperStyle,
          )}
          style={{ backgroundColor: '#1A1A1A', borderRadius: 12 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            onClick={() => setDropdownState('main')}
            className='flex items-center gap-2 mb-6 cursor-pointer'
          >
            <motion.button
              whileTap={{ scale: 0.95 }}
              className='text-white bg-[#3C3B3E] flex justify-center items-center rounded-full w-6 h-6'
            >
              <ChevronLeftIcon className='text w-5 h-5 ' />
            </motion.button>
            <span className='font-semibold -translate-y-[1px]'>Embed Code</span>
          </div>
          <div className='h-px bg-zinc-800 my-4' />
          <div className='space-y-4'>
            <div>
              <TextArea
                ref={textareaRef}
                value={`<iframe src="${embedUrl}" allow="fullscreen;" width="100%" height="600px" title="PDF Reader" loading="lazy" sandbox="allow-same-origin allow-scripts allow-popups"></iframe>`}
                className='min-h-[120px] bg-zinc-800 border-zinc-700 text-white font-mono text-sm resize-none'
                readOnly
              />
            </div>
            <Button
              onClick={() => handleCopyEmbedLink(embedUrl)}
              className='w-full bg-orange-600 hover:bg-orange-700 text-white h-10 rounded-md flex items-center justify-center relative overflow-hidden'
            >
              <CopyFilled className='w-4 h-4 mr-2' />
              <AnimatePresence mode='wait' initial={false}>
                {copied ? (
                  <motion.span
                    key='copied'
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -20, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    Copied!
                  </motion.span>
                ) : (
                  <motion.span
                    key='default'
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -20, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    Copy to clipboard
                  </motion.span>
                )}
              </AnimatePresence>
            </Button>
            <div className='text-xs text-zinc-400 mt-2'>
              Copy this code and paste it into your website or blog to embed
              this content.
            </div>
          </div>
        </motion.div>
      );
    }
    if (dropdownState === 'copied') {
      return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className='p-6 w-full h-full  flex justify-between  flex-col rounded-t-xl md:max-h-[70vh] rounded-xl'
          style={{ backgroundColor: '#1A1A1A', borderRadius: 12 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            onClick={() => setDropdownState('main')}
            className='flex items-center gap-2 mb-6 cursor-pointer'
          >
            <motion.button
              whileTap={{ scale: 0.95 }}
              className='text-white bg-[#3C3B3E] flex justify-center items-center rounded-full w-6 h-6'
            >
              <ChevronLeftIcon className='text w-5 h-5 ' />
            </motion.button>
            <span className='font-semibold -translate-y-[1px]'>
              Copy to clipboard
            </span>
          </div>
          <div className='flex flex-col'>
            <div className='flex flex-col items-center justify-center py-4 mb-4'>
              <motion.div
                className='w-12 h-12 rounded-full bg-green-600 flex items-center justify-center mb-2'
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', damping: 15 }}
              >
                <svg
                  viewBox='0 0 24 24'
                  fill='none'
                  className='w-6 h-6 text-white'
                >
                  <motion.path
                    d='M5 13l4 4L19 7'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                  />
                </svg>
              </motion.div>
              <div className='font-semibold'>Copied to clipboard</div>
            </div>
            <Button
              className='w-full bg-transparent border-[1px] border-white h-10 text-white rounded-full hover:bg-gray-700'
              onClick={() => {
                setDropdownState('main');
                setIsModalVisible(false);
                onModalClose?.();
              }}
            >
              Continue Reading
            </Button>
          </div>
        </motion.div>
      );
    }

    if (dropdownState === 'howItWorks') {
      return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={clsx(
            originalContainerParentStyle,
            modalContentWrapperStyle,
          )}
          style={{ backgroundColor: '#1A1A1A', borderRadius: 12 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            onClick={() => setDropdownState('main')}
            className='flex items-center gap-2 mb-6 cursor-pointer'
          >
            <motion.button
              whileTap={{ scale: 0.95 }}
              className='text-white bg-[#3C3B3E] flex justify-center items-center rounded-full w-6 h-6'
            >
              <ChevronLeftIcon className='text w-5 h-5 ' />
            </motion.button>
            <span className='font-semibold -translate-y-[1px]'>
              How referral works
            </span>
          </div>
          <div className='flex flex-col space-y-6'>
            <AnimatePresence>
              {REFERRAL_STEPS.map((step, index) => (
                <motion.div
                  key={index}
                  className={clsx(
                    step.icon === 'coin' ? 'items-start' : 'items-center',
                    'flex gap-4',
                  )}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.15 }}
                >
                  <ReferralStepIcon icon={step.icon} />
                  <div className='flex flex-col'>
                    <div className='text-sm'>{step.text}</div>
                    <ShouldRender check={step.icon === 'coin'}>
                      <Button
                        onClick={() => router.push('/rewards')}
                        className='bg-white text-black w-fit font-semibold text-xs rounded-[8px] max-h-[32px] mt-3 hover:bg-gray-200'
                      >
                        Get Products for free
                      </Button>
                    </ShouldRender>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>
      );
    }

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.3 }}
        className={clsx(
          originalContainerParentStyle,
          modalContentWrapperStyle,
          'customScrollbarShare',
        )}
        style={{
          backgroundColor: '#1A1A1A',
          borderRadius: 12,
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='flex justify-between items-start md:items-center mb-4'>
          <div>
            <h1 className='text-base md:text-xl font-semibold'>
              Earn{' '}
              <span className='font-bold' style={{ color: '#FF5722' }}>
                100 Z-coins
              </span>{' '}
              by sharing
            </h1>
            <Button
              className='bg-[#282829] border-none mt-3 rounded-[8px] max-h-[32px] text-sm font-semibold text-white hover:bg-grayZl-850'
              onClick={handleHowItWorks}
            >
              How it works?
            </Button>
          </div>
          <ShouldRender check={!screens.lg || isFullScreen}>
            <div>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  onModalClose?.();
                  setDropdownState('main');
                }}
                className='bg-[#282829] border-none bg-[#FFFFFF1A] px-2 rounded-full text-sm font-semibold'
              >
                <XIcon className='w-4 h-4' />
              </Button>
            </div>
          </ShouldRender>
        </div>
        <Divider className='border-[#282829] my-5 md:my-6' />
        <div>
          <h2 className='text-sm font-medium'>Share with People</h2>
          <div className='flex gap-2 mt-2'>
            <Input
              value={shareUrl}
              readOnly
              placeholder='https://share.example.com/xyz123'
              style={{
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderColor: 'transparent',
                color: 'white',
              }}
              className='h-10'
            />
            <Button
              type='primary'
              loading={isCopyingLink}
              onClick={handleCopyShareLink}
              style={{ backgroundColor: '#FF5722', borderColor: '#FF5722' }}
              className='h-10 '
            >
              <div className='px-3 font-semibold text-base'>Copy</div>
            </Button>
          </div>
        </div>
        <div className='mt-6'>
          <h2 className='text-sm font-medium pb-2'>Quick Actions</h2>
          <div className='flex items-center bg-neutral-900 rounded-md'>
            <Input
              type='text'
              placeholder="Enter receiver's Email/Phone"
              value={quickActionInput}
              onChange={(e) => setQuickActionInput(e.target.value)}
              style={{
                backgroundColor: 'transparent',
                borderColor: '#282829',
                color: 'white',
              }}
              suffix={
                <Button
                  type='text'
                  size='small'
                  icon={<ArrowRightOutlined />}
                  onClick={handleQuickActionSubmit}
                  loading={loadingQuickAction}
                  className='text-white bg-[#282829] rounded-full hover:bg-gray-600'
                />
              }
              className='h-10'
            />
          </div>
        </div>
        <Divider className='border-[#282829] my-5 md:my-6' />
        {SOCIAL_SECTIONS.map((section, index) => (
          <motion.div
            className='mb-4'
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <h3 className='text-sm font-medium mb-4'>{section.title}</h3>
            <Row gutter={[16, 16]}>
              {section.items.slice(0, 4).map((item, i) => (
                <Col span={6} key={i}>
                  <SocialIcon
                    name={item.name}
                    icon={item.icon || ''}
                    color={item.color}
                  />
                </Col>
              ))}
            </Row>
            {section.items.length > 4 && (
              <Row gutter={[16, 16]} className='mt-4'>
                {section.items.slice(4).map((item, i) => (
                  <Col span={6} key={i}>
                    <SocialIcon
                      name={item.name}
                      icon={item.icon || ''}
                      color={item.color}
                    />
                  </Col>
                ))}
              </Row>
            )}
            <ShouldRender check={section.title !== 'More Options'}>
              <Divider className='border-[#282829] my-5 md:my-6' />
            </ShouldRender>
          </motion.div>
        ))}
      </motion.div>
    );
  };
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setPageTitle(document.title);
    }
  }, []);

  const handleShareClick = (platform: string) => {
    const eventParams: EventDetails = {
      event_name: 'share_product',
      source: 'books',
      source_type: samplePdf ? 'short' : 'long',
      source_id: extractId(bookId as string) as string,
      source_name: ecomProductData?.display_name
        ? ecomProductData?.display_name
        : pageTitle
          ? normalizeProductName(pageTitle)
          : '',
      sub_source: null,
      sub_source_id: null,
      unit: 'boolean',
      value: 1,
      meta: {
        click_source: 'books_reading_screen',
        click_action: platform,
      },
    };
    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
  };

  const openShareModal = () => {
    if (!isLoggedIN) {
      setAuthModalVisible((prevState) => ({ ...prevState, login: true }));
      notify('Please login to share content', 'warning');
      return;
    }
    setIsModalVisible(true);
    setDropdownState('main');
    togglePlayer?.();
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setDropdownState('main');
    onModalClose?.();
  };
  const SOCIAL_SECTIONS = [
    {
      title: 'Social Share',
      items: [
        { color: '#1877F2', name: 'Facebook', icon: facebook },
        { color: '#0A66C2', name: 'LinkedIn', icon: linkedIn },
        { color: '#1DA1F2', name: 'X(Twitter)', icon: Tumblr },
        { color: '#E60023', name: 'Pinterest', icon: pinterest },
        { color: '#FF0000', name: 'YouTube', icon: youtube },
        { color: '#5865F2', name: 'Discord', icon: discordIcon },
        { color: '#FF4500', name: 'Reddit', icon: redit },
        { color: '#35465C', name: 'Tumblr', icon: twitter },
        { color: '#35465C', name: 'LinkedIn Page', icon: linkedInBuilding },
      ],
    },
    {
      title: 'Messaging',
      items: [
        { color: '#EA4335', name: 'Email', icon: email },
        { color: '#25D366', name: 'WhatsApp', icon: WhatsApp },
        { color: '#0088CC', name: 'iMessage', icon: iMessage },
        {
          color: '#0088CC',
          name: 'Facebook Messenger',
          icon: FacebookMessenger,
        },
        { color: '#FF5722', name: 'Microsoft Teams', icon: MicrosoftTeams },
        { color: '#FF5722', name: 'Telegram', icon: Telegram },
        { color: '#1DA1F2', name: 'Slack', icon: slack },
        { color: '#1DA1F2', name: 'Skype', icon: skype },
      ],
    },
    {
      title: 'More Options',
      items: [
        ...(isPdfReader
          ? [
              {
                color: '#333333',
                name: 'Embed',
                icon: codeSlash,
              },
            ]
          : []),
        { color: '#333333', name: 'Copy to clipboard', icon: copyFile },
        { color: '#333333', name: 'Affiliate Marketing', icon: sitemap },
      ],
    },
  ];

  const modalWidth = isFullScreen
    ? '90vw'
    : screens.lg
      ? '512px'
      : screens.md
        ? '60vw'
        : '100vw';
  let modalHeight = 'auto';
  if (isModalVisible) {
    if (isFullScreen) {
      modalHeight =
        dropdownState === 'main'
          ? '98vh'
          : dropdownState === 'howItWorks'
            ? '60vh'
            : '50vh';
    } else if (!screens.md) {
      modalHeight =
        dropdownState === 'main'
          ? '100vh'
          : dropdownState === 'howItWorks'
            ? '60vh'
            : '50vh';
    }
  }

  return (
    <>
      {contextHolder}
      <ShareButtonRender
        type={type}
        openShareModal={openShareModal}
        isReadingScreen={isReadingScreen}
        noBg={noBg}
        screens={screens}
        isPdfReader={true}
      />
      <ShouldRender check={!isFullScreen}>
        <Modal
          open={isModalVisible}
          onCancel={handleModalCancel}
          footer={null}
          title={null}
          centered
          width={modalWidth}
          destroyOnHidden
          closable={false}
          maskClosable={true}
          bodyStyle={{
            padding: 0,
            margin: 0,
            backgroundColor: 'transparent',
            height: screens.lg && !isFullScreen ? 'auto' : modalHeight,
            maxHeight: screens.lg && !isFullScreen ? '80vh' : undefined,
          }}
          className='m-0 relative'
          classNames={{
            content: 'p-0 w-full md:rounded-xl',
          }}
          wrapClassName={clsx(
            'share-modal-wrapper',
            isFullScreen ? 'fullscreen-share-modal' : '',
            !screens.md ? 'mobile-share-modal' : '',
          )}
        >
          {renderModalContent()}
          <ShouldRender check={screens.lg}>
            <div className='absolute -right-[8.5%] top-0'>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  onModalClose?.();
                  setDropdownState('main');
                }}
                className=' border-none bg-grayZl-900 hover:bg-grayZl-850 px-2 rounded-full text-sm font-semibold'
              >
                <XIcon className='w-4 h-4 text-white' />
              </Button>
            </div>
          </ShouldRender>
        </Modal>
      </ShouldRender>
      <ShouldRender check={isFullScreen}>
        <Drawer
          placement={isFullScreen ? 'right' : 'bottom'}
          onClose={() => {
            setDropdownState('main');
          }}
          closable={false}
          title={undefined}
          open={isModalVisible}
          {...(isFullScreen && { getContainer: false })}
          className={clsx(
            'no-scrollbar',
            isFullScreen ? 'rounded-none z-[1000]' : 'rounded-t-xl ',
            'share-drawer bg-[#1A1A1A]',
          )}
          height={
            isFullScreen
              ? '100vh'
              : dropdownState === 'main'
                ? '98vh'
                : dropdownState === 'howItWorks'
                  ? '60vh'
                  : '50vh'
          }
        >
          {renderModalContent()}
        </Drawer>
      </ShouldRender>
    </>
  );
};

export default ShareButton;
